import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookOpen, Download, Plus, Eye } from "lucide-react";
import { MemoryBookStatusBadge, RoadTo2027Badge, CopfApprovedBadge } from "@/components/MemoryBookStatusBadge";
import api from "@/lib/api";
import { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function MyMemoryBooksPage() {
  const { user, token } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      nav("/connexion?next=/mes-memoires");
      return;
    }
    (async () => {
      try {
        const res = await api.get("/memory-books/requests/mine");
        setItems(res.data?.items || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, nav]);

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6" data-testid="my-memory-books-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-semibold">Mes livrets AITO</h1>
          <p className="text-sm text-muted-foreground">
            Suivez l'avancement de vos demandes et téléchargez vos livrets terminés.
          </p>
        </div>
        <Button asChild data-testid="my-memory-books-new-btn">
          <Link to="/memoires/nouvelle-demande">
            <Plus className="h-4 w-4 mr-2" /> Nouvelle demande
          </Link>
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Chargement…</p>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center space-y-3">
            <BookOpen className="h-10 w-10 mx-auto text-muted-foreground" />
            <h2 className="text-lg font-medium">Aucun livret pour le moment</h2>
            <p className="text-sm text-muted-foreground">
              Demandez votre premier livret : portrait, bilan annuel, carnet Jeux du Pacifique 2027…
            </p>
            <Button asChild>
              <Link to="/memoires/nouvelle-demande">Demander un livret</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <Card key={it.id} className="aito-card-hover" data-testid={`my-memorybook-row-${it.id}`}>
              <CardContent className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{it.entity_name_snapshot || "Livret"}</span>
                    <MemoryBookStatusBadge status={it.status} />
                    {it.road_to_2027 && <RoadTo2027Badge />}
                    {it.status === "copf_approved" && <CopfApprovedBadge />}
                  </div>
                  <p className="text-xs text-muted-foreground font-mono">
                    Demandé le {new Date(it.created_at).toLocaleDateString("fr-FR")}
                    {it.period_year ? ` · Période ${it.period_year}` : ""}
                    {it.latest_version_number ? ` · v${it.latest_version_number}` : ""}
                  </p>
                  {it.context && <p className="text-xs text-muted-foreground italic">{it.context}</p>}
                </div>
                <div className="flex items-center gap-2">
                  {it.current_version_id && (
                    <a
                      href={`${API}/memory-books/versions/${it.current_version_id}/pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        // Redirection avec header Auth impossible via <a> ; on télécharge via api
                        e.preventDefault();
                        api
                          .get(`/memory-books/versions/${it.current_version_id}/pdf`, { responseType: "blob" })
                          .then((res) => {
                            const blob = new Blob([res.data], { type: "application/pdf" });
                            const url = URL.createObjectURL(blob);
                            window.open(url, "_blank");
                          });
                      }}
                    >
                      <Button variant="outline" size="sm" data-testid={`memorybook-download-pdf-button-${it.id}`}>
                        <Download className="h-4 w-4 mr-1" /> PDF
                      </Button>
                    </a>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
